import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Paperclip, Check } from "lucide-react";
import { toast } from "sonner";
import { STATUS_LABEL, APPLIANCE_TYPES, findLabel, formatDateTime } from "@/lib/options";

const MEDIA_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const mediaProxyUrl = (url) => `${MEDIA_BASE}/media/twilio?url=${encodeURIComponent(url)}`;

export const MessageMedia = ({ message, customerId }) => {
  const [saving, setSaving] = useState(false);
  const [savedIdx, setSavedIdx] = useState({});
  const [pickerFor, setPickerFor] = useState(null); // media index with open picker
  const [tickets, setTickets] = useState(null);

  const media = message.media || [];
  if (media.length === 0) return null;

  const openPicker = async (idx) => {
    setPickerFor(idx);
    if (tickets === null && customerId) {
      try {
        const { data } = await api.get(`/customers/${customerId}/history`);
        setTickets(data.tickets || []);
      } catch (e) {
        toast.error(formatApiError(e));
        setTickets([]);
      }
    }
  };

  const saveTo = async (idx, ticketId) => {
    setSaving(true);
    try {
      await api.post(`/messages/${message.id}/save-media`, { media_index: idx, ticket_id: ticketId });
      toast.success("Photo saved to ticket attachments");
      setSavedIdx((s) => ({ ...s, [idx]: true }));
      setPickerFor(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2 mt-1">
      {media.map((md, idx) => {
        const isImage = !md.content_type || md.content_type.startsWith("image/");
        const src = mediaProxyUrl(md.url);
        return (
          <div key={idx} data-testid={`msg-media-${message.id}-${idx}`}>
            {isImage ? (
              <a href={src} target="_blank" rel="noreferrer">
                <img src={src} alt="Customer attachment" loading="lazy"
                  className="rounded-lg max-h-64 max-w-full border border-slate-200 bg-white" />
              </a>
            ) : (
              <a href={src} target="_blank" rel="noreferrer" className="text-xs underline flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> Attachment ({md.content_type || "file"})
              </a>
            )}
            {message.direction === "inbound" && (
              savedIdx[idx] ? (
                <div className="text-[11px] text-emerald-700 flex items-center gap-1 mt-1">
                  <Check className="w-3 h-3" /> Saved to ticket
                </div>
              ) : pickerFor === idx ? (
                <div className="flex items-center gap-1 mt-1">
                  <Select onValueChange={(tid) => saveTo(idx, tid)} disabled={saving}>
                    <SelectTrigger className="h-7 text-xs w-56" data-testid={`save-media-select-${message.id}-${idx}`}>
                      <SelectValue placeholder={tickets === null ? "Loading tickets…" : "Choose ticket…"} />
                    </SelectTrigger>
                    <SelectContent>
                      {(tickets || []).map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {findLabel(APPLIANCE_TYPES, t.appliance_type)} · {t.scheduled_at ? formatDateTime(t.scheduled_at) : "unscheduled"} · {STATUS_LABEL[t.status] || t.status}
                        </SelectItem>
                      ))}
                      {tickets !== null && tickets.length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-500">No tickets for this customer</div>
                      )}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setPickerFor(null)}>Cancel</Button>
                </div>
              ) : (
                <button onClick={() => openPicker(idx)}
                  className="text-[11px] text-blue-700 underline mt-1 flex items-center gap-1"
                  data-testid={`save-media-btn-${message.id}-${idx}`}>
                  <Paperclip className="w-3 h-3" /> Save to ticket
                </button>
              )
            )}
          </div>
        );
      })}
    </div>
  );
};
